import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

type PartyType = "Supplier" | "Customer" | "Regulator" | "Partner" | "Other";

const ALL_TYPES: PartyType[] = ["Supplier", "Customer", "Regulator", "Partner", "Other"];

export function PartyNew() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [partyName, setPartyName] = useState("");
  const [partyTypes, setPartyTypes] = useState<PartyType[]>([]);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [relationshipSummary, setRelationshipSummary] = useState("");
  const [theirRequirements, setTheirRequirements] = useState("");
  const [ourRequirements, setOurRequirements] = useState("");
  const [isComplianceObligation, setIsComplianceObligation] = useState(false);
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [createdUuid, setCreatedUuid] = useState<string | null>(null);

  const toggleType = (t: PartyType) => {
    setPartyTypes((curr) => (curr.includes(t) ? curr.filter((x) => x !== t) : [...curr, t]));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);
    try {
      const result = await api<{ uuid: string }>("/interested-parties", {
        method: "POST",
        body: {
          partyName,
          partyTypes,
          contactName: contactName.trim() || null,
          contactEmail: contactEmail.trim() || null,
          contactPhone: contactPhone.trim() || null,
          websiteUrl: websiteUrl.trim() || null,
          relationshipSummary: relationshipSummary.trim() || null,
          theirRequirements: theirRequirements.trim() || null,
          ourRequirements: ourRequirements.trim() || null,
          isComplianceObligation,
          notes: notes.trim() || null,
        },
      });
      setCreatedUuid(result.uuid);
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (createdUuid) {
    return <Navigate to={`/o/${slug}/parties/${createdUuid}`} replace />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Back
        </button>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Add interested party
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Capture the basics now; you can return later to fill in the §4.2 needs and expectations. A
          party can be more than one type at once (a supplier who is also a customer, for example).
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label
            htmlFor="party-name"
            className="block text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Party name
          </label>
          <input
            id="party-name"
            type="text"
            required
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
            aria-invalid={errors.length > 0 || undefined}
            aria-describedby={errors.length > 0 ? "party-errors" : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Type (one or more)
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {ALL_TYPES.map((t) => {
              const checked = partyTypes.includes(t);
              return (
                <label
                  key={t}
                  className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                    checked
                      ? "border-brand-500 bg-brand-50 text-brand-900 dark:bg-brand-500/10 dark:text-brand-100"
                      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleType(t)}
                    className="sr-only"
                  />
                  {t}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="party-contact-name"
            label="Contact name"
            value={contactName}
            onChange={setContactName}
            errorPresent={errors.length > 0}
          />
          <Field
            id="party-contact-email"
            label="Contact email"
            type="email"
            value={contactEmail}
            onChange={setContactEmail}
            errorPresent={errors.length > 0}
          />
          <Field
            id="party-contact-phone"
            label="Contact phone"
            value={contactPhone}
            onChange={setContactPhone}
            errorPresent={errors.length > 0}
          />
          <Field
            id="party-website"
            label="Website"
            type="url"
            value={websiteUrl}
            onChange={setWebsiteUrl}
            errorPresent={errors.length > 0}
          />
        </div>

        <TextArea
          id="party-relationship"
          label="Relationship summary"
          hint="What does this party do for you, or you for them? Free text."
          value={relationshipSummary}
          onChange={setRelationshipSummary}
        />

        <TextArea
          id="party-their-reqs"
          label="Their needs and expectations of us"
          hint="ISO §4.2: what does this party expect from your organisation? E.g., on-time delivery, certificates, complaint handling."
          value={theirRequirements}
          onChange={setTheirRequirements}
        />

        <TextArea
          id="party-our-reqs"
          label="Our requirements of them"
          hint="What do you require from this party? E.g., quality SLAs, security questionnaires, accreditation evidence."
          value={ourRequirements}
          onChange={setOurRequirements}
        />

        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            checked={isComplianceObligation}
            onChange={(e) => setIsComplianceObligation(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-slate-700 dark:text-slate-200">
            This party imposes a compliance obligation
            <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
              Tick if their requirements include statutory, regulatory or contractual obligations
              that you must meet (feeds the legal register).
            </span>
          </span>
        </label>

        <TextArea
          id="party-notes"
          label="Notes"
          hint="Anything else worth recording. Internal use only."
          value={notes}
          onChange={setNotes}
        />

        {errors.length > 0 ? (
          <div
            id="party-errors"
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          >
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="list-disc space-y-0.5 pl-4">
                {errors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting || partyTypes.length === 0 || partyName.trim().length === 0}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Save party"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  errorPresent,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "url";
  errorPresent: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={errorPresent || undefined}
        aria-describedby={errorPresent ? "party-errors" : undefined}
        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      />
    </div>
  );
}

function TextArea({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
      />
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
    </div>
  );
}
